import React from 'react';
import { ArrowDownTrayIcon, EyeIcon, TrashIcon } from '@heroicons/react/24/outline';

/**
 * DocumentCard - Enhanced document display card with status and actions
 */
const DocumentCard = ({ document, onDownload, onPreview, onDelete }) => {
    const getFileIcon = (fileName) => {
        if (!fileName) return '📄';
        const ext = fileName.split('.').pop()?.toLowerCase();
        if (['pdf'].includes(ext)) return '📕';
        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return '🖼️';
        if (['doc', 'docx'].includes(ext)) return '📘';
        if (['xls', 'xlsx'].includes(ext)) return '📊';
        if (['ppt', 'pptx'].includes(ext)) return '📙';
        return '📄';
    };

    const getStatusStyle = (status) => {
        const normalized = (status || '').toLowerCase();
        if (normalized === 'approved' || normalized === 'verified') {
            return 'bg-emerald-100 text-emerald-700 border-emerald-200';
        }
        if (normalized === 'pending' || normalized === 'under_review') {
            return 'bg-amber-100 text-amber-700 border-amber-200';
        }
        if (normalized === 'rejected') {
            return 'bg-rose-100 text-rose-700 border-rose-200';
        }
        if (normalized === 'required') {
            return 'bg-blue-100 text-blue-700 border-blue-200';
        }
        return 'bg-slate-100 text-slate-600 border-slate-200';
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            return new Date(dateStr).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch {
            return dateStr;
        }
    };

    const isImage = (fileName) => {
        if (!fileName) return false;
        const ext = fileName.split('.').pop()?.toLowerCase();
        return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
    };

    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group">
            <div className="flex items-start gap-4">
                {/* File icon or thumbnail */}
                <div className="h-14 w-14 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center text-2xl flex-shrink-0 group-hover:bg-indigo-50 transition-colors">
                    {isImage(document.title || document.file_name) && document.thumbnail_url ? (
                        <img
                            src={document.thumbnail_url}
                            alt=""
                            className="h-full w-full object-cover rounded-xl"
                        />
                    ) : (
                        getFileIcon(document.title || document.file_name)
                    )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <h4 className="text-sm font-semibold text-slate-900 truncate group-hover:text-indigo-600 transition-colors">
                                {document.title || document.file_name || 'Document'}
                            </h4>
                            <p className="text-xs text-slate-500 mt-0.5">
                                {formatDate(document.submitted_at || document.created_at)}
                                {document.type && ` • ${document.type}`}
                            </p>
                        </div>
                        <span className={`flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full border ${getStatusStyle(document.status)}`}>
                            {document.status || 'Draft'}
                        </span>
                    </div>

                    {/* Feedback for rejected */}
                    {document.status?.toLowerCase() === 'rejected' && document.feedback && (
                        <div className="mt-2 p-2 bg-rose-50 rounded-lg text-xs text-rose-700">
                            <strong>Feedback:</strong> {document.feedback}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        {onPreview && (
                            <button
                                onClick={() => onPreview(document)}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            >
                                <EyeIcon className="h-3.5 w-3.5" />
                                Preview
                            </button>
                        )}
                        {onDownload && (
                            <button
                                onClick={() => onDownload(document)}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                                Download
                            </button>
                        )}
                        {onDelete && (
                            <button
                                onClick={() => onDelete(document)}
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                            >
                                <TrashIcon className="h-3.5 w-3.5" />
                                Delete
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DocumentCard;
